import 'whatwg-fetch';
import qs from 'qs';
import api from 'qa/api';
import { csrfFromDom } from 'common/utils/server';
import { notifySuccess, notifyError } from 'common/state/notificationState';

const FETCH_FORMS_REQUEST = 'formsPage/FETCH_FORMS_REQUEST';
const FETCH_FORMS_SUCCESS = 'formsPage/FETCH_FORMS_SUCCESS';
const EDIT_FORM = 'formsPage/EDIT_FORM';
const REMOVE_FORM = 'formsPage/REMOVE_FORM';
const RESTORE_FORM = 'formsPage/RESTORE_FORM';
const TOGGLE_ARCHIVED = 'formsPage/TOGGLE_ARCHIVED';
const TABLE_LOADING = 'formsPage/TABLE_LOADING';
const SET_PAGE = 'formsPage/SET_PAGE';
const SET_TYPE = 'formsPage/SET_TYPE';
const SET_SEARCH = 'formsPage/SET_SEARCH';
const SET_STATES = 'formsPage/SET_STATES';
const SET_SORT = 'formsPage/SET_SORT';

export const SCORECARD_STATES = {
  draft: 'draft',
  published: 'published',
  archived: 'archived',
};

export const DEFAULT_SCORECARD_STATES = [SCORECARD_STATES.draft, SCORECARD_STATES.published];

// Selectors

export const getForms = state => state.qaForms.forms;
export const getLoading = state => state.qaForms.loading;
export const getArchived = state => state.qaForms.archived;
export const getPage = state => state.qaForms.page;
export const getTotalPages = state => state.qaForms.totalPages;
export const getType = state => state.qaForms.type;
export const getSearch = state => state.qaForms.search;
export const getStates = state => state.qaForms.states;
export const getOrderBy = state => state.qaForms.orderBy;
export const getOrderDirection = state => state.qaForms.orderDirection;
export const getLifecycleEnabled = state => Boolean(state.featureFlags?.qa_scorecard_lifecycle);

// Action Creators

const fetchRequest = () => ({
  type: FETCH_FORMS_REQUEST,
});

const fetchFormsSuccess = ({ qa_forms, meta }) => ({
  type: FETCH_FORMS_SUCCESS,
  forms: qa_forms,
  totalPages: meta.total_pages,
});

export const tableLoading = () => ({
  type: TABLE_LOADING,
});

export const editForm = formId => ({
  type: EDIT_FORM,
  formId,
});

export const removeQaForm = formId => ({
  type: REMOVE_FORM,
  formId,
});

export const restoreQaForm = form => ({
  type: RESTORE_FORM,
  form,
});

export const toggleArchived = () => ({
  type: TOGGLE_ARCHIVED,
});

export const setPage = page => ({
  type: SET_PAGE,
  page,
});

export const setType = scorecardType => ({
  type: SET_TYPE,
  scorecardType,
});

export const setSearch = search => ({
  type: SET_SEARCH,
  search,
});

export const setStates = states => ({
  type: SET_STATES,
  states,
});

export const setSort = (orderBy, orderDirection) => ({
  type: SET_SORT,
  orderBy,
  orderDirection,
});

// Thunks

export const fetchQAForms = options => (dispatch, getState) => {
  // Sort lives in the store so it survives the refetches triggered by paging,
  // searching and filtering; callers may still override it per request.
  const {
    orderBy = getOrderBy(getState()),
    orderDirection = getOrderDirection(getState()),
    per = 10,
  } = options || {};
  const archived = getArchived(getState());
  const page = getPage(getState());
  const type = getType(getState());
  const lifecycleEnabled = getLifecycleEnabled(getState());
  const search = getSearch(getState());
  const states = getStates(getState()) ?? DEFAULT_SCORECARD_STATES;
  // An empty selection reads as "All states" in the filter, so spell every state out.
  // A blank `state` param is indistinguishable from no param at all, and the endpoint
  // then falls back to its pre-lifecycle archived filter, hiding archived scorecards.
  const selectedStates = states.length ? states : Object.values(SCORECARD_STATES);
  // With the flag off nothing new is sent, so the endpoint behaves exactly as it does
  // today. With it on, `state` replaces `archived` and carries the whole selection.
  const lifecycleParams =
    lifecycleEnabled ?
      {
        include_lifecycle_actions: true,
        name: search || undefined,
        state: selectedStates.join(','),
      }
    : { archived };
  const params = qs.stringify({
    order_by: orderBy,
    order_direction: orderDirection,
    include_reviews_count: true,
    type,
    per,
    page,
    ...lifecycleParams,
  });
  const url = `/api/v1/qa_forms?${params}`;

  dispatch(fetchRequest());

  return fetch(url)
    .then(result => result.json())
    .then(result => {
      dispatch(fetchFormsSuccess(result || {}));

      return result;
    });
};

export const duplicateQAForm = formId => dispatch => {
  const url = `/api/v1/qa_forms/${formId}/duplicate`;

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-token': csrfFromDom(),
    },
  })
    .then(result => {
      if (!result.ok) {
        throw Error(result.statusText);
      }
      return result.json();
    })
    .then(json => {
      window.location.assign(`/qa/forms/${json.id}`);
    })
    .catch(e => dispatch(notifyError(e.message)));
};

export const archiveQAForm =
  (formId, archiveReviews = false) =>
  (dispatch, getState) => {
    const url = `qa_forms/${formId}/archive?archive_reviews=${archiveReviews}`;

    return api
      .fetch(dispatch, url, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRF-token': csrfFromDom() },
      })
      .then(() => {
        dispatch(notifySuccess('Scorecard archived successfully.'));

        if (getLifecycleEnabled(getState())) return dispatch(fetchQAForms());

        return dispatch(removeQaForm(formId));
      });
  };

export const unarchiveQAForm = formId => dispatch => {
  const url = `/qa_forms/${formId}/unarchive`;

  return api
    .fetch(dispatch, url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
    })
    .then(([form]) => {
      dispatch(restoreQaForm(form));
      dispatch(notifySuccess('Scorecard restored successfully.'));
    });
};

const transitionQAForm = (formId, event, successMessage) => dispatch => {
  const url = `qa_forms/${formId}/${event}`;

  return api
    .fetch(dispatch, url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
    })
    .then(result => {
      // A rejected transition has already been reported by api.fetch, which resolves
      // with { errors } rather than rejecting.
      if (result?.errors) return result;

      dispatch(notifySuccess(successMessage));
      // The row may no longer belong in the active state filter, so refetch rather
      // than patching it in place.
      return dispatch(fetchQAForms());
    });
};

export const publishQAForm = formId =>
  transitionQAForm(formId, 'publish', 'Scorecard published successfully.');

export const unpublishQAForm = formId =>
  transitionQAForm(formId, 'unpublish', 'Scorecard returned to draft.');

export const returnQAFormToDraft = formId =>
  transitionQAForm(formId, 'return_to_draft', 'Scorecard returned to draft.');

// Reducers

const initialState = {
  forms: [],
  totalPages: 0,
  page: 1,
  archived: false,
  search: '',
  states: DEFAULT_SCORECARD_STATES,
  orderBy: 'created_at',
  orderDirection: 'desc',
  editingFormId: null,
  loading: false,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_FORMS_REQUEST: {
      return { ...state, loading: true };
    }

    case TABLE_LOADING: {
      return { ...state, loading: true };
    }

    case TOGGLE_ARCHIVED: {
      return {
        ...state,
        archived: !state.archived,
      };
    }

    case FETCH_FORMS_SUCCESS: {
      return {
        ...state,
        forms: action.forms,
        totalPages: action.totalPages,
        loading: false,
      };
    }

    case REMOVE_FORM: {
      return {
        ...state,
        forms: state.forms.filter(form => form.id != action.formId),
      };
    }

    case RESTORE_FORM: {
      return {
        ...state,
        forms: state.forms.map(form => {
          return form.id === action.form.id ? action.form : form;
        }),
      };
    }

    case SET_PAGE: {
      return {
        ...state,
        page: action.page,
      };
    }

    case SET_TYPE: {
      return {
        ...state,
        type: action.scorecardType,
      };
    }

    case SET_SEARCH: {
      return {
        ...state,
        search: action.search,
      };
    }

    case SET_STATES: {
      return {
        ...state,
        states: action.states,
      };
    }

    case SET_SORT: {
      return {
        ...state,
        orderBy: action.orderBy,
        orderDirection: action.orderDirection,
      };
    }

    case EDIT_FORM: {
      return {
        ...state,
        editingFormId: action.formId,
      };
    }

    default: {
      return state;
    }
  }
};
